import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStudy } from '../context/StudyContext';
import { apiService } from '../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Question = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { dispatch, actionTypes, topics } = useStudy();
  
  const { questions, sessionLength, difficulty, selectedTopics, sessionId } = location.state || {};
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [timerActive, setTimerActive] = useState(true);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [currentExplanation, setCurrentExplanation] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const [currentQuestions, setCurrentQuestions] = useState(questions || []);
  const [loadingNextQuestion, setLoadingNextQuestion] = useState(false);

  const Icons = {
    back: <i className="mdi mdi-arrow-left"></i>,
    pause: <i className="mdi mdi-pause"></i>,
    hint: <i className="mdi mdi-help-circle"></i>,
    volume: <i className="mdi mdi-volume-high"></i>,
    correct: <i className="mdi mdi-check"></i>,
    incorrect: <i className="mdi mdi-close"></i>,
    time: <i className="mdi mdi-clock"></i>,
    brain: <i className="mdi mdi-brain"></i>,
    question: <i className="mdi mdi-help-circle-outline"></i>,
    suggestion: <i className="mdi mdi-alert-circle"></i>,
    check: <i className="mdi mdi-check-circle"></i>,
    thumbUp: <i className="mdi mdi-thumb-up"></i>,
    thumbDown: <i className="mdi mdi-thumb-down"></i>
  };

  const ConfidenceIcons = {
    high: <i className="mdi mdi-emoticon-happy" style={{ fontSize: '24px', color: '#ffffff' }}></i>,
    medium: <i className="mdi mdi-emoticon-neutral" style={{ fontSize: '24px', color: '#ffffff' }}></i>,
    low: <i className="mdi mdi-emoticon-sad" style={{ fontSize: '24px', color: '#ffffff' }}></i>
  };

  const AnswerIcons = {
    correct: <i className="mdi mdi-check-circle" style={{ fontSize: '24px', color: '#28a745', lineHeight: '1' }}></i>,
    incorrect: <i className="mdi mdi-close-circle" style={{ fontSize: '24px', color: '#dc3545', lineHeight: '1' }}></i>
  };

  const StatsIcons = {
    correct: <i className="mdi mdi-check-circle"></i>,
    incorrect: <i className="mdi mdi-close-circle"></i>,
    time: <i className="mdi mdi-clock"></i>,
    confidence: <i className="mdi mdi-brain"></i>
  };

  useEffect(() => {
    console.log('Session ID from location.state:', sessionId);
  }, [sessionId]);

  const calculateProgress = () => {
    if (!sessionLength) return 0;
    const answeredQuestions = showExplanation ? currentQuestionIndex + 1 : currentQuestionIndex;
    return Math.min((answeredQuestions / sessionLength) * 100, 100);
  };

  const isSessionCompleted = () => {
    return currentQuestionIndex >= sessionLength - 1 && showExplanation;
  };

  const handleBack = () => {
    setShowBackConfirm(true);
  };

  const confirmEndSession = () => {
    navigate('/results', { 
      state: { 
        answers, 
        questions: currentQuestions.slice(0, currentQuestionIndex + 1), 
        sessionLength, 
        endedEarly: true,
        totalTime: answers.reduce((sum, answer) => sum + answer.timeSpent, 0),
        sessionId: sessionId
      } 
    });
  };

  const cancelEndSession = () => {
    setShowBackConfirm(false);
  };

  useEffect(() => {
    setQuestionStartTime(Date.now());
    setTimerActive(true);
    setCurrentExplanation(null);
  }, [currentQuestionIndex]);

  useEffect(() => {
    let timer;
    if (timerActive) {
      timer = setInterval(() => {
      }, 1000);
    }
    
    return () => clearInterval(timer);
  }, [timerActive]);

  const getCorrectAnswerIndex = (correctAnswer, options) => {
    if (typeof correctAnswer === 'number') {
      return Math.max(0, Math.min(correctAnswer, options.length - 1));
    }
    
    if (typeof correctAnswer === 'string') {
      const letter = correctAnswer.toLowerCase().trim();
      
      const letterMap = {
        'a': 0,
        'b': 1, 
        'c': 2,
        'd': 3
      };
      
      if (letter in letterMap) {
        return letterMap[letter];
      }
      
      const matchingIndex = options.findIndex(option => 
        option.toLowerCase().includes(correctAnswer.toLowerCase()) ||
        correctAnswer.toLowerCase().includes(option.toLowerCase())
      );
      
      if (matchingIndex !== -1) {
        return matchingIndex;
      }
      
      return 0; 
    }
    
    return 0; 
  };

  const getLetterFromIndex = (index) => {
    const letterMap = {
      0: 'a',
      1: 'b',
      2: 'c', 
      3: 'd'
    };
    return letterMap[index] || 'a';
  };

  const formatSeconds = (seconds) => {
    if (seconds < 60) {
      return `${seconds} seconds`;
    } else {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}m ${secs}s`;
    }
  };

  const handleAnswerSelect = (index) => {
    if (!showExplanation && !isSubmitting) {
      setSelectedAnswer(index);
    }
  };

  const getAnswerOptionStyles = (index) => {
    const baseCardStyle = {
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: '#e0e0e0',
      opacity: 1,
      position: 'relative',
      transition: 'all 0.2s ease'
    };
    
    const baseRadioStyle = {
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: '#666666',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      flexShrink: 0,
      transition: 'all 0.2s ease'
    };
    
    if (showExplanation) {
      if (index === correctAnswerIndex) {
        return {
          cardStyle: {
            ...baseCardStyle,
            backgroundColor: 'rgba(40, 167, 69, 0.05)',
            borderColor: '#28a745'
          },
          radioStyle: {
            ...baseRadioStyle,
            borderColor: '#28a745',
            backgroundColor: '#28a745'
          }
        };
      } else if (index === selectedAnswer && selectedAnswer !== correctAnswerIndex) {
        return {
          cardStyle: {
            ...baseCardStyle,
            backgroundColor: 'rgba(220, 53, 69, 0.05)',
            borderColor: '#dc3545'
          },
          radioStyle: {
            ...baseRadioStyle,
            borderColor: '#dc3545',
            backgroundColor: '#dc3545'
          }
        };
      }
    } else if (selectedAnswer === index && !showExplanation) {
      return {
        cardStyle: {
          ...baseCardStyle,
          backgroundColor: 'rgba(44, 90, 160, 0.05)',
          borderColor: '#2c5aa0'
        },
        radioStyle: {
          ...baseRadioStyle,
          borderColor: '#2c5aa0',
          backgroundColor: '#2c5aa0'
        }
      };
    }
    
    return {
      cardStyle: baseCardStyle,
      radioStyle: baseRadioStyle
    };
  };

  const handleConfidenceSelect = async (confidence) => {
    if (!showExplanation && !isSubmitting && selectedAnswer !== null) {
      const question = currentQuestions[currentQuestionIndex];
      const timeSpent = Math.round((Date.now() - questionStartTime) / 1000);
      setIsSubmitting(true);
      
      // Show processing toast with spinner
      const toastId = toast.info(
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid #e0e0e0',
            borderTop: '2px solid #2c5aa0',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span>Processing your answer...</span>
        </div>,
        {
          autoClose: false,
          closeButton: false,
          draggable: false
        }
      );
      
      try {
        const userAnswerLetter = getLetterFromIndex(selectedAnswer);
        
        if (!question.id) {
          throw new Error('Question ID is missing');
        }
        
        if (!userAnswerLetter || !['a', 'b', 'c', 'd'].includes(userAnswerLetter)) {
          throw new Error('Invalid answer selection');
        }
        
        if (!['high', 'medium', 'low'].includes(confidence)) {
          throw new Error('Invalid confidence level');
        }
        
        const result = await apiService.submitAnswer(
          question.id.toString(),
          userAnswerLetter.toLowerCase().trim(),
          confidence,
          timeSpent,
          sessionId
        );
        
        console.log('Server response:', result);
        
        if (!result) {
          throw new Error('Empty response from server');
        }
        
        let aiExplanation = 'No AI summary available.';
        let explanationText = result.explanation || 'No explanation available.';
        
        if (result.ai_summary && result.ai_summary.explanantion) {
          aiExplanation = result.ai_summary.explanantion;
          if (result.ai_summary.key_take_aways) {
            aiExplanation += `\n\nKey takeaways: ${result.ai_summary.key_take_aways}`;
          }
        }
        
        const correctAnswerIndex = getCorrectAnswerIndex(
          result.correct_answer, 
          question.options
        );
        
        const isCorrect = result.is_correct;
        
        const topic = topics.find(t => t.api_topic === question.topic);
        
        dispatch({
          type: actionTypes.ANSWER_QUESTION,
          payload: {
            questionId: question.id,
            answerIndex: selectedAnswer,
            isCorrect,
            timeSpent,
            topicId: topic?.id,
            confidenceLevel: confidence
          }
        });
        
        setAnswers([...answers, {
          questionId: question.id,
          selectedAnswer,
          isCorrect,
          timeSpent,
          questionText: question.question_text,
          correctAnswer: correctAnswerIndex,
          options: question.options,
          topic: question.topic,
          explanation: explanationText,
          aiSummary: aiExplanation,
          confidenceLevel: confidence,
          mastery: result.mastery || 'unknown',
          difficulty: result.difficulty || question.difficulty || 'medium',
          sessionId: sessionId
        }]);
        
        setCurrentExplanation({
          explanation: explanationText,
          aiSummary: aiExplanation,
          correctAnswerIndex: correctAnswerIndex,
          isCorrect: isCorrect,
          feedback: result.feedback,
          confidenceLevel: confidence,
          mastery: result.mastery || 'unknown'
        });
        
        setShowExplanation(true);
        setTimerActive(false);
        
        // Dismiss processing toast and show success
        toast.dismiss(toastId);
        toast.success('Answer submitted successfully!', {
          autoClose: 2000
        });
        
      } catch (error) {
        console.error('Error submitting answer:', error);
        
        // Dismiss processing toast
        toast.dismiss(toastId);
        
        let userErrorMessage = error.message;
        try {
          const parsedError = JSON.parse(error.message);
          userErrorMessage = typeof parsedError === 'object' ? JSON.stringify(parsedError) : parsedError;
        } catch (e) {
          userErrorMessage = error.message;
        }
        
        // Show error toast
        toast.error(`Failed to submit answer: ${userErrorMessage}. Please try again.`, {
          autoClose: 5000
        });
        
        const topic = topics.find(t => t.api_topic === question.topic);
        
        dispatch({
          type: actionTypes.ANSWER_QUESTION,
          payload: {
            questionId: question.id,
            answerIndex: selectedAnswer,
            isCorrect: false,
            timeSpent,
            topicId: topic?.id,
            confidenceLevel: confidence
          }
        });
        
        setAnswers([...answers, {
          questionId: question.id,
          selectedAnswer,
          isCorrect: false,
          timeSpent,
          questionText: question.question_text,
          correctAnswer: 0, 
          options: question.options,
          topic: question.topic,
          explanation: `Error: ${error.message}`,
          aiSummary: `Error: ${error.message}`,
          confidenceLevel: confidence,
          mastery: 'error',
          difficulty: question.difficulty || 'medium',
          sessionId: sessionId
        }]);
        
        setCurrentExplanation({
          explanation: `Error: ${error.message}`,
          aiSummary: `Error: ${error.message}`,
          correctAnswerIndex: 0,
          isCorrect: false,
          feedback: 'Submission error occurred',
          confidenceLevel: confidence,
          mastery: 'error'
        });
        
        setShowExplanation(true);
        setTimerActive(false);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

const fetchNextQuestion = async () => {
  if (location.state?.isMockTest && sessionId) {
    console.log('Fetching next mock test question for session:', sessionId);
    try {
      const nextQuestion = await apiService.getNextMockQuestion(sessionId);
      console.log('Next mock question received:', nextQuestion);
      return nextQuestion;
    } catch (error) {
      console.error('Error fetching next mock question:', error);
      return null;
    }
  }
  
  if (!selectedTopics || selectedTopics.length === 0) {
    console.error('No topics selected for regular study session');
    return null;
  }

  try {
    const firstTopic = selectedTopics[0];
    const topic = topics.find(t => t.id === firstTopic);
    
    if (!topic) {
      console.error('Topic not found');
      return null;
    }

    const selectedDifficulty = difficulty || 'medium';
    
    console.log(`Fetching next question for session: ${sessionId}`);
    
    const nextQuestion = await apiService.getNextQuestion(
      topic.api_topic, 
      selectedDifficulty, 
      sessionId
    );
    
    console.log('Next question received via apiService:', nextQuestion);
    return nextQuestion;
  } catch (error) {
    console.error('Error fetching next question:', error);
    return null;
  }
};

const handleNext = async () => {
  if (currentQuestionIndex < sessionLength - 1) {
    setLoadingNextQuestion(true);
    
    try {
      const nextQuestion = await fetchNextQuestion();
      
      if (nextQuestion) {
        const updatedQuestions = [...currentQuestions, nextQuestion];
        setCurrentQuestions(updatedQuestions);
        
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setSelectedAnswer(null);
        setShowExplanation(false);
        setCurrentExplanation(null);
        setTimerActive(true);
        setQuestionStartTime(Date.now()); 
      } else {
        console.warn('Could not fetch next question, ending session');
        alert('No more questions available. Ending session.');
        navigate('/results', { 
          state: { 
            answers, 
            questions: currentQuestions.slice(0, currentQuestionIndex + 1),
            sessionLength,
            totalTime: answers.reduce((sum, answer) => sum + answer.timeSpent, 0),
            sessionId: sessionId,
            isMockTest: location.state?.isMockTest 
          } 
        });
      }
    } catch (error) {
      console.error('Error handling next question:', error);
      alert('Error loading next question. Ending session.');
      navigate('/results', { 
        state: { 
          answers, 
          questions: currentQuestions.slice(0, currentQuestionIndex + 1),
          sessionLength,
          totalTime: answers.reduce((sum, answer) => sum + answer.timeSpent, 0),
          sessionId: sessionId,
          isMockTest: location.state?.isMockTest 
        } 
      });
    } finally {
      setLoadingNextQuestion(false);
    }
  } else {
    navigate('/results', { 
      state: { 
        answers, 
        questions: currentQuestions.slice(0, currentQuestionIndex + 1),
        sessionLength,
        totalTime: answers.reduce((sum, answer) => sum + answer.timeSpent, 0),
        sessionId: sessionId,
        isMockTest: location.state?.isMockTest 
      } 
    });
  }
};

  if (!currentQuestions || currentQuestions.length === 0 || currentQuestionIndex >= currentQuestions.length) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning text-center">
          No questions available. Please adjust your study session filters.
        </div>
        <div className="text-center">
          <button className="btn btn-primary" onClick={() => navigate('/custom-study')}>
            Back to Study Session Setup
          </button>
        </div>
      </div>
    );
  }

  const question = currentQuestions[currentQuestionIndex];
  const correctAnswerIndex = currentExplanation?.correctAnswerIndex ?? 0;

  return (
    <div style={{ 
      backgroundColor: '#f8f9fa', 
      minHeight: '100vh',
      fontFamily: '"Open Sans", sans-serif'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://cdn.materialdesignicons.com/7.4.47/css/materialdesignicons.min.css" rel="stylesheet" />
      
      <ToastContainer 
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{ 
          top: '80px',
          fontSize: '16px',
          fontWeight: '600',
          zIndex: 9999
        }}
        toastStyle={{
          backgroundColor: '#ffffff',
          color: '#333',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          padding: '16px 20px',
          minHeight: '60px',
          fontSize: '15px',
          fontWeight: '500',
          border: '2px solid #e0e0e0'
        }}
      />
      
      {showBackConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '10px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            border: '1px solid #e0e0e0'
          }}>
            <h3 style={{ marginBottom: '16px', color: '#2c5aa0', fontFamily: '"Open Sans", sans-serif', fontWeight: '600' }}>End Session?</h3>
            <p style={{ marginBottom: '24px', color: '#666', fontFamily: '"Open Sans", sans-serif' }}>
              Are you sure you want to end this study session? Your progress will be saved.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={cancelEndSession}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #2c5aa0',
                  borderRadius: '6px',
                  backgroundColor: 'transparent',
                  color: '#2c5aa0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: '"Open Sans", sans-serif',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#2c5aa0';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#2c5aa0';
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmEndSession}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#2c5aa0',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: '"Open Sans", sans-serif',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#1e3d6f'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#2c5aa0'}
              >
                End Session
              </button>
            </div>
          </div>
        </div>
      )}

      <header style={{ 
        backgroundColor: '#2c5aa0', 
        height: '60px', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center'
      }}>
        <div style={{ 
          width: '100%', 
          maxWidth: '800px', 
          margin: '0 auto',
          padding: '0 20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <button 
              onClick={handleBack}
              style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '20px', 
                backgroundColor: 'rgba(255,255,255,0.1)', 
                border: 'none',
                marginRight: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
            >
              <div style={{ color: 'white', width: '24px', height: '24px' }}>
                {Icons.back}
              </div>
            </button>
            
            <div style={{ flexGrow: 1, position: 'relative', marginRight: '15px' }}>
              <div style={{ 
                height: '6px', 
                borderRadius: '3px', 
                backgroundColor: 'rgba(255,255,255,0.3)', 
                opacity: 1,
                overflow: 'hidden'
              }}>
                <div style={{ 
                  backgroundColor: isSessionCompleted() ? '#28a745' : 'white',
                  height: '100%',
                  width: `${calculateProgress()}%`,
                  borderRadius: '3px',
                  transition: 'width 0.3s ease, background-color 0.3s ease'
                }}></div>
              </div>
              <div style={{
                position: 'absolute',
                top: '17px',
                left: '325px',
                transform: 'translate(-50%, -50%)',
                fontSize: '12px',
                color: 'white',
                fontWeight: '600',
                pointerEvents: 'none',
                fontFamily: '"Open Sans", sans-serif'
              }}>
                {currentQuestionIndex + 1} / {sessionLength}
              </div>
            </div>
            
            <div 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                marginLeft: '-3px', 
                zIndex: 1000, 
                backgroundColor: isSessionCompleted() ? '#28a745' : 'rgba(255,255,255,0.2)', 
                width: '44px', 
                height: '44px', 
                borderRadius: '22px',
                transition: 'background-color 0.3s ease',
                border: isSessionCompleted() ? 'none' : '2px solid rgba(255,255,255,0.3)'
              }}
            >
              <div style={{ 
                color: 'white', 
                width: '30px', 
                height: '24px',
                opacity: 10,
                marginLeft: '12px',
              }}>
                {isSessionCompleted() ? Icons.check : Icons.pause}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div style={{ paddingTop: '30px', flex: 1 }}>
        <div style={{ maxWidth: '768px', margin: '0 auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '0 20px 100px' }}>
            
              {loadingNextQuestion && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 9999
                }}>
                  <div style={{
                    backgroundColor: 'white',
                    padding: '40px',
                    borderRadius: '12px',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)',
                    textAlign: 'center',
                    border: '1px solid #e0e0e0',
                    minWidth: '200px'
                  }}>
                    <div style={{ marginBottom: '20px' }}>
                      <div style={styles.spinner}></div>
                    </div>
                    <div style={{ 
                      color: '#2c5aa0', 
                      fontWeight: '600', 
                      fontSize: '16px',
                      fontFamily: '"Open Sans", sans-serif' 
                    }}>
                      Loading...
                    </div>
                  </div>
                </div>
              )}
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', width: '100%', alignItems: 'center' }}>
                    <div style={{ flexGrow: 1, userSelect: 'none' }}>
                      <div style={{ 
                        textAlign: 'left', 
                        fontSize: '18px', 
                        lineHeight: '26px', 
                        marginTop: '10px', 
                        marginBottom: '10px',
                        color: '#333333',
                        fontFamily: '"Open Sans", sans-serif',
                        fontWeight: '500'
                      }}>
                        {question.question_text}
                      </div>
                    </div>
                  </div>
                  <div style={{ 
                    color: '#666666', 
                    direction: 'ltr', 
                    marginTop: '10px',
                    fontSize: '14px',
                    fontFamily: '"Open Sans", sans-serif'
                  }}>
                    Select the best answer
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '960px', gap: '12px' }}>
                {question.options.map((option, index) => {
                  const { cardStyle, radioStyle } = getAnswerOptionStyles(index);
                  
                  return (
                    <div key={index} style={cardStyle}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderRadius: '8px',
                        borderWidth: '2px',
                        borderStyle: 'solid',
                        borderColor: cardStyle.borderColor,
                        pointerEvents: 'none'
                      }}></div>
                      <div>
                        <div 
                          style={{ 
                            padding: '16px 20px', 
                            cursor: showExplanation ? 'auto' : 'pointer', 
                            overflow: 'hidden',
                            opacity: showExplanation && index !== correctAnswerIndex && index !== selectedAnswer ? 0.6 : 1
                          }}
                          onClick={() => handleAnswerSelect(index)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: showExplanation ? 'default' : 'pointer' }}>
                              <div style={radioStyle}>
                                {selectedAnswer === index && !showExplanation && (
                                  <div style={{ 
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: 'white'
                                  }}></div>
                                )}
                                {showExplanation && index === correctAnswerIndex && (
                                  <div style={{ 
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: 'white'
                                  }}></div>
                                )}
                                {showExplanation && index === selectedAnswer && selectedAnswer !== correctAnswerIndex && (
                                  <div style={{ 
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: 'white'
                                  }}></div>
                                )}
                              </div>
                            </div>
                            <div style={{ flexGrow: 1, marginLeft: '15px' }}>
                              <div style={{ 
                                textAlign: 'left', 
                                fontSize: '16px', 
                                lineHeight: '22px', 
                                marginTop: '0px', 
                                marginBottom: '0px',
                                color: '#333333',
                                fontFamily: '"Open Sans", sans-serif',
                                fontWeight: '400'
                              }}>
                                {option}
                              </div>
                            </div>
                            <div style={{ 
                              margin: '0px 0px 0px 20px', 
                              alignSelf: 'center', 
                              color: '#40484c', 
                              height: '34px', 
                              minWidth: '60px'
                            }}>
                              {showExplanation && (
                                <div style={{ 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '27px',
                                  height: '27px'
                                }}>
                                  {index === correctAnswerIndex ? (
                                    <div style={{ 
                                      color: '#28a745', 
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      {AnswerIcons.correct}
                                    </div>
                                  ) : index === selectedAnswer && selectedAnswer !== correctAnswerIndex ? (
                                    <div style={{ 
                                      color: '#dc3545', 
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      {AnswerIcons.incorrect}
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex' }}>
              <div style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ margin: '0px 0px 0px 16px', alignSelf: 'flex-start' }}>
                   
                  </div>
                </div>
              </div>
            </div>

            {!showExplanation && selectedAnswer !== null && (
              <div style={{
                marginTop: '20px',
                padding: '20px',
                border: '1px solid #e0e0e0',
                borderRadius: '10px',
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
              }}>
                <div style={{ display: 'flex', textAlign: 'center', gap: '15px' }}>
                  <div style={{ flex: 1 }}>
                    <button 
                      aria-label="Confidence level low"
                      tabIndex="0"
                      data-testid="confidence-button-low"
                      disabled={isSubmitting}
                      style={{
                        display: 'flex',
                        backgroundColor: '#dc3545',
                        border: 'none',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '30px',
                        flex: '1 1 0%',
                        width: '100%',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        opacity: isSubmitting ? 0.6 : 1,
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => handleConfidenceSelect('low')}
                      onMouseEnter={(e) => !isSubmitting && (e.target.style.transform = 'translateY(-2px)')}
                      onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                      <div dir="auto" className="css-146c3p1 r-lrvibr" style={{ 
                        fontSize: '24px', 
                        color: '#ffffff',
                        fontFamily: 'material-community',
                        fontWeight: 'normal',
                        fontStyle: 'normal'
                      }}>
                        {ConfidenceIcons.low}
                      </div>
                    </button>
                    
                  </div>

                  <div style={{ flex: 1 }}>
                    <button 
                      aria-label="Confidence level medium"
                      tabIndex="0"
                      data-testid="confidence-button-medium"
                      disabled={isSubmitting}
                      style={{
                        display: 'flex',
                        backgroundColor: '#ffc107',
                        border: 'none',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '30px',
                        flex: '1 1 0%',
                        width: '100%',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        opacity: isSubmitting ? 0.6 : 1,
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => handleConfidenceSelect('medium')}
                      onMouseEnter={(e) => !isSubmitting && (e.target.style.transform = 'translateY(-2px)')}
                      onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                      <div dir="auto" className="css-146c3p1 r-lrvibr" style={{ 
                        fontSize: '24px', 
                        color: '#ffffff',
                        fontFamily: 'material-community',
                        fontWeight: 'normal',
                        fontStyle: 'normal'
                      }}>
                        {ConfidenceIcons.medium}
                      </div>
                    </button>
                   
                  </div>

                  <div style={{ flex: 1 }}>
                    <button 
                      aria-label="Confidence level high"
                      tabIndex="0"
                      data-testid="confidence-button-high"
                      disabled={isSubmitting}
                      style={{
                        display: 'flex',
                        backgroundColor: '#28a745',
                        border: 'none',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '30px',
                        flex: '1 1 0%',
                        width: '100%',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        opacity: isSubmitting ? 0.6 : 1,
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => handleConfidenceSelect('high')}
                      onMouseEnter={(e) => !isSubmitting && (e.target.style.transform = 'translateY(-2px)')}
                      onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                      <div dir="auto" className="css-146c3p1 r-lrvibr" style={{ 
                        fontSize: '24px', 
                        color: '#ffffff',
                        fontFamily: 'material-community',
                        fontWeight: 'normal',
                        fontStyle: 'normal'
                      }}>
                        {ConfidenceIcons.high}
                      </div>
                    </button>
          
                  </div>
                </div>
              </div>
            )}

            {showExplanation && currentExplanation && (
              <div style={{ marginTop: '20px' }}>
                <div style={{ 
                  border: '1px solid #e0e0e0',
                  padding: '20px',
                  borderRadius: '10px',
                  marginBottom: '20px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-around' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', flex: 1, justifyContent: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', maxWidth: '120px', cursor: 'help' }}>
                        <div style={{ 
                          color: currentExplanation.isCorrect ? '#28a745' : '#dc3545',
                          marginRight: '10px',
                          width: '24px',
                          height: '24px'
                        }}>
                          {currentExplanation.isCorrect ? Icons.thumbUp : Icons.thumbDown}
                        </div>
                        <div>
                          <div style={{ 
                            direction: 'ltr',
                            color: '#333333',
                            fontFamily: '"Open Sans", sans-serif',
                            letterSpacing: '0.5px',
                            fontWeight: '600',
                            lineHeight: '16px',
                            fontSize: '14px'
                          }}>
                            {currentExplanation.isCorrect ? 'Correct' : 'Incorrect'}
                          </div>
                          <div style={{ 
                            fontFamily: '"Open Sans", sans-serif',
                            direction: 'ltr',
                            fontSize: '12px',
                            color: '#666666'
                          }}>
                            ANSWERED
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'row', flex: 1, justifyContent: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', maxWidth: '120px', cursor: 'help' }}>
                        <div style={{ 
                          color: '#2c5aa0',
                          marginRight: '10px',
                          width: '24px',
                          height: '24px'
                        }}>
                          {StatsIcons.time}
                        </div>
                        <div>
                          <div style={{ 
                            direction: 'ltr',
                            color: '#333333',
                            fontFamily: '"Open Sans", sans-serif',
                            letterSpacing: '0.5px',
                            fontWeight: '600',
                            lineHeight: '16px',
                            fontSize: '14px'
                          }}>
                            {formatSeconds(answers[answers.length - 1]?.timeSpent || 0)}
                          </div>
                          <div style={{ 
                            fontFamily: '"Open Sans", sans-serif',
                            direction: 'ltr',
                            fontSize: '12px',
                            color: '#666666'
                          }}>
                            SPEED
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'row', flex: 1, justifyContent: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', maxWidth: '120px', cursor: 'help' }}>
                        <div style={{ 
                          color: '#2c5aa0',
                          marginRight: '10px',
                          width: '24px',
                          height: '24px'
                        }}>
                          {StatsIcons.confidence}
                        </div>
                        <div>
                          <div style={{ 
                            direction: 'ltr',
                            color: '#333333',
                            fontFamily: '"Open Sans", sans-serif',
                            letterSpacing: '0.5px',
                            fontWeight: '600',
                            lineHeight: '16px',
                            fontSize: '14px',
                            textTransform: 'capitalize'
                          }}>
                            {currentExplanation.confidenceLevel}
                          </div>
                          <div style={{ 
                            fontFamily: '"Open Sans", sans-serif',
                            direction: 'ltr',
                            fontSize: '12px',
                            color: '#666666'
                          }}>
                            CONFIDENCE
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}>
                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ 
                          direction: 'ltr',
                          fontFamily: '"Open Sans", sans-serif',
                          letterSpacing: '0.1px',
                          fontWeight: '600',
                          lineHeight: '20px',
                          fontSize: '18px',
                          color: '#2c5aa0'
                        }}>Explanation</div>
                      </div>
                      <div style={{ cursor: 'help' }}>
                        <button style={{ 
                          backgroundColor: 'transparent',
                          width: '34px',
                          height: '34px',
                          borderRadius: '17px',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          <div style={{ color: '#2c5aa0', width: '18px', height: '18px' }}>
                            {Icons.volume}
                          </div>
                        </button>
                      </div>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
                        <div style={{ width: '24px', height: '24px' }}>
                          {AnswerIcons.correct}
                        </div>
                        <div style={{ flexGrow: 1 }}>
                          <div style={{ 
                            textAlign: 'left',
                            fontSize: '16px',
                            fontWeight: '600',
                            fontFamily: '"Open Sans", sans-serif',
                            lineHeight: '22px',
                            color: '#333333'
                          }}>
                            {question.options[correctAnswerIndex]}
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        {currentExplanation.aiSummary && currentExplanation.aiSummary !== 'No AI summary available.' ? (
                          <div>
                            {currentExplanation.aiSummary.split('\n').map((line, index) => {
                              const cleanLine = line.replace(/\*\*/g, '').trim();
                              
                              if (cleanLine.startsWith('Key takeaways:')) {
                                return (
                                  <div key={index} style={{ marginTop: '20px', marginBottom: '15px' }}>
                                    <h4 style={{
                                      color: '#2c5aa0',
                                      fontSize: '16px',
                                      fontWeight: '600',
                                      fontFamily: '"Open Sans", sans-serif',
                                      borderBottom: '2px solid #2c5aa0',
                                      paddingBottom: '8px',
                                      margin: '0 0 15px 0'
                                    }}>
                                      Key Takeaways:
                                    </h4>
                                    <div style={{
                                      fontSize: '14px',
                                      lineHeight: '22px',
                                      color: '#333333',
                                      fontFamily: '"Open Sans", sans-serif'
                                    }}>
                                      {cleanLine.replace('Key takeaways:', '').trim()}
                                    </div>
                                  </div>
                                );
                              }
                              
                              const headingMatch = cleanLine.match(/^(Explanation|Key takeaways|Confidence level|Correct answer)(?::|$)/i);
                              if (headingMatch) {
                                const headingText = headingMatch[1];
                                return (
                                  <div key={index} style={{ marginTop: index > 0 ? '20px' : '0', marginBottom: '15px' }}>
                                    <h4 style={{
                                      color: '#2c5aa0',
                                      fontSize: '16px',
                                      fontWeight: '600',
                                      fontFamily: '"Open Sans", sans-serif',
                                      borderBottom: '2px solid #2c5aa0',
                                      paddingBottom: '8px',
                                      margin: '0 0 15px 0'
                                    }}>
                                      {headingText}:
                                    </h4>
                                  </div>
                                );
                              } 
                              else if (cleanLine.startsWith('*') || cleanLine.startsWith('•') || cleanLine.match(/^\d+\./) || cleanLine.startsWith('-')) {
                                return (
                                  <div key={index} style={{ 
                                    display: 'flex', 
                                    alignItems: 'flex-start', 
                                    marginBottom: '10px',
                                    paddingLeft: '20px'
                                  }}>
                                    <span style={{ marginRight: '12px', color: '#2c5aa0', minWidth: '8px', fontWeight: 'bold' }}>•</span>
                                    <span style={{
                                      fontSize: '14px',
                                      lineHeight: '22px',
                                      color: '#333333',
                                      fontFamily: '"Open Sans", sans-serif',
                                      flex: 1
                                    }}>
                                      {cleanLine.replace(/^[*•\d.\-\s]+/, '')}
                                    </span>
                                  </div>
                                );
                              } 
                              else if (cleanLine.includes('->')) {
                                const [key, value] = cleanLine.split('->').map(part => part.trim());
                                return (
                                  <div key={index} style={{ 
                                    display: 'flex', 
                                    marginBottom: '10px',
                                    alignItems: 'flex-start'
                                  }}>
                                    <strong style={{ 
                                      color: '#2c5aa0', 
                                      minWidth: '140px',
                                      fontSize: '14px',
                                      fontFamily: '"Open Sans", sans-serif',
                                      fontWeight: '600'
                                    }}>
                                      {key}:
                                    </strong> 
                                    <span style={{ 
                                      marginLeft: '15px',
                                      color: '#333333',
                                      fontFamily: '"Open Sans", sans-serif',
                                      fontSize: '14px',
                                      lineHeight: '22px',
                                      flex: 1
                                    }}>
                                      {value}
                                    </span>
                                  </div>
                                );
                              } 
                              else if (cleanLine.trim() === '') {
                                return <div key={index} style={{ height: '15px' }} />;
                              } 
                              else {
                                return (
                                  <div key={index}>
                                    {index === 0 && (
                                      <div style={{ marginBottom: '15px' }}>
                                        <h4 style={{
                                          color: '#2c5aa0',
                                          fontSize: '16px',
                                          fontWeight: '600',
                                          fontFamily: '"Open Sans", sans-serif',
                                          borderBottom: '2px solid #2c5aa0',
                                          paddingBottom: '8px',
                                          margin: '0 0 15px 0'
                                        }}>
                                          Explanation:
                                        </h4>
                                      </div>
                                    )}
                                    <p style={{
                                      textAlign: 'left',
                                      fontSize: '14px',
                                      lineHeight: '22px',
                                      color: '#333333',
                                      fontFamily: '"Open Sans", sans-serif',
                                      marginBottom: '15px'
                                    }}>
                                      {cleanLine}
                                    </p>
                                  </div>
                                );
                              }
                            })}
                          </div>
                        ) : (
                          <div>
                            <div style={{ marginBottom: '15px' }}>
                              <h4 style={{
                                color: '#2c5aa0',
                                fontSize: '16px',
                                fontWeight: '600',
                                fontFamily: '"Open Sans", sans-serif',
                                borderBottom: '2px solid #2c5aa0',
                                paddingBottom: '8px',
                                margin: '0 0 15px 0'
                              }}>
                                Explanation:
                              </h4>
                            </div>
                            <p style={{
                              textAlign: 'left',
                              fontSize: '14px',
                              lineHeight: '22px',
                              color: '#333333',
                              fontFamily: '"Open Sans", sans-serif',
                              marginBottom: '15px'
                            }}>
                              {currentExplanation.explanation}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {question.options.map((option, index) => {
                      if (index !== correctAnswerIndex) {
                        return (
                          <div key={index} style={{ display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                            <div style={{ width: '27px', height: '27px' }}>
                              {AnswerIcons.incorrect}
                            </div>
                            <div style={{ flexGrow: 1 }}>
                              <div style={{ 
                                textAlign: 'left',
                                fontSize: '14px',
                                fontWeight: '500',
                                fontFamily: '"Open Sans", sans-serif',
                                lineHeight: '21px',
                                color: '#333333'
                              }}>
                                {option}
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                  <button style={{ 
                    backgroundColor: 'transparent',
                    border: '1px solid #2c5aa0',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'white';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#2c5aa0';
                  }}
                  >
                    <div style={{ color: '#2c5aa0', width: '18px', height: '18px', marginRight: '8px' }}>
                      {Icons.suggestion}
                    </div>
                    <span style={{ 
                      color: '#2c5aa0',
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>Suggest correction</span>
                  </button>
                </div>
              </div>
            )}

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
              {showExplanation && (
                <button
                  onClick={handleNext}
                  disabled={loadingNextQuestion}
                  style={{
                    backgroundColor: '#2c5aa0',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '12px 30px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#ffffff',
                    cursor: loadingNextQuestion ? 'not-allowed' : 'pointer',
                    opacity: loadingNextQuestion ? 0.6 : 1,
                    fontFamily: '"Open Sans", sans-serif',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!loadingNextQuestion) {
                      e.target.style.backgroundColor = '#1e3d6f';
                      e.target.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loadingNextQuestion) {
                      e.target.style.backgroundColor = '#2c5aa0';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {loadingNextQuestion ? 'Loading...' : 
                  currentQuestionIndex < sessionLength - 1 ? 'Next Question' : 'View Results'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
    </div>
  );
};

const styles = {
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #2c5aa0',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto'
  }
};

export default Question;